import { useState } from 'react'
import { Link } from 'react-router-dom'
import Badge from 'components/Badge/Badge'
import { fetchBBookLive } from 'services/bBookServices'
import { signedMoney } from '../report/pnlCharts'
import { euro } from '../report/charts'
import { usePolling, useT } from '../hooks'
import { recall, remember } from '../cache'
import BBookAlertBanners from './BBookAlertBanners'
import BBookKpi from './BBookKpi'
import BBookInfo from './BBookInfo'
import BBookIcon from './BBookIcon'

const REFRESH_MS = 15000
const WARN_SHARE = 0.7 // amber from 70 % of the cap, red at the cap
const LEVEL_COLOR = { success: 'var(--bb-gain)', warning: 'var(--bb-warn)', danger: 'var(--bb-loss)' }
const percentOf = (share) => `${(share * 100).toFixed(share < 0.1 ? 1 : 0).replace('.', ',')} %`

// Net exposure against the high cap, the ratio the engine's brake trips on at 100 %. Number and target are printed, not left to the colour.
const ExposureGauge = ({ exposure, t }) => {
  const { net, high, low, share_of_cap: share } = exposure
  if (share === null) return <p className='b_book_hint'>{t('pages.b_book_pnl.live.exposure_unknown')}</p>
  const level = share >= 1 ? 'danger' : share >= WARN_SHARE ? 'warning' : 'success'

  return (
    <div className='b_book_pnl_gauge'>
      <div className='b_book_pnl_gauge_head'>
        <strong className='b_book_num'>{percentOf(share)}</strong>
        <span>{t('pages.b_book_pnl.live.exposure_of', { net: euro(net), high: euro(high) })}</span>
        <span className={`b_book_level b_book_level_${level}`}>{t(`pages.b_book_pnl.live.level_${level}`)}</span>
      </div>
      <div className='b_book_pnl_gauge_track' role='meter' aria-label={t('pages.b_book_pnl.live.exposure')}
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(share * 100)} aria-valuetext={`${percentOf(share)}, ${t(`pages.b_book_pnl.live.level_${level}`)}`}>
        <div className='b_book_pnl_gauge_fill' style={{ transform: `scaleX(${Math.min(share, 1)})`, backgroundColor: LEVEL_COLOR[level] }} />
        {low > 0 && high > 0 && <div className='b_book_pnl_gauge_marker' style={{ left: `${Math.min(low / high, 1) * 100}%` }} />}
      </div>
      <p className='b_book_hint'>{t('pages.b_book_pnl.live.exposure_hint', { low: euro(low) })}</p>
    </div>
  )
}

// Everything the engine publishes to redis, refreshed every 15 s while the tab is visible.
const BBookLive = ({ user }) => {
  const { t } = useT()
  const [live, setLive] = useState(() => recall('live') ?? null) // the last figures paint at once, the poll replaces them
  const [failed, setFailed] = useState(false)

  usePolling(async () => {
    try {
      setLive(remember('live', await fetchBBookLive(user)))
      setFailed(false)
    } catch (e) {
      setFailed(true) // keep the last figures on screen, flagged as stale
    }
  }, REFRESH_MS, [user])

  if (!live) return failed ? <p className='b_book_error_text' role='alert'>{t('pages.b_book_pnl.live.error')}</p> : <div className='b_book_skeleton' aria-hidden='true' />

  const { status, engine, unrealized, realized, funded_margin } = live
  const fundedHint = !funded_margin ? null
    : funded_margin.cap > 0 ? t('pages.b_book_pnl.live.funded_of_cap', { cap: euro(funded_margin.cap), share: percentOf(funded_margin.share_of_cap) })
    : t('pages.b_book_pnl.live.funded_no_cap')
  const heartbeatAge = status.engine_heartbeat ? Math.max(0, Math.round((new Date(live.at) - status.engine_heartbeat) / 1000)) : null
  const engineUp = status.engine_alive && status.engine_listening
  const badges = [
    { key: 'enabled', text: t(`pages.b_book.status.${status.enabled ? 'enabled' : 'disabled'}`), good: status.enabled, hint: t('pages.b_book_pnl.live.enabled_hint') },
    { key: 'engine', text: t(`pages.b_book.brakes.engine_${engineUp ? 'on' : 'off'}`), good: engineUp, hint: t('pages.b_book.brakes.engine_hint') },
    { key: 'force_a_book', text: t(`pages.b_book.brakes.force_a_book_${status.force_a_book ? 'on' : 'off'}`), good: !status.force_a_book, hint: t('pages.b_book.brakes.force_a_book_hint') },
    { key: 'over_max_loss', text: t(`pages.b_book.brakes.over_max_loss_${status.over_max_loss ? 'on' : 'off'}`), good: !status.over_max_loss, hint: t('pages.b_book.brakes.over_max_loss_hint') },
    { key: 'dlq', text: t('pages.b_book_pnl.live.dlq', { count: engine.dlq_fills }), good: engine.dlq_fills === 0, hint: t('pages.b_book_pnl.live.dlq_hint') },
  ]
  const tripped = badges.filter(({ good }) => !good)

  return (
    <section aria-labelledby='b-book-live-title' className={failed ? 'b_book_report_refreshing' : ''}>
      <div className='b_book_report_section b_book_pnl_live_head'>
        <h2 id='b-book-live-title'>{t('pages.b_book_pnl.live.title')}</h2>
        <span className={`b_book_live_stamp ${failed ? 'b_book_live_stamp_stale' : ''}`}>
          <span className='b_book_live_dot' aria-hidden='true' />
          {failed ? t('pages.b_book_pnl.live.stale') : t('pages.b_book_pnl.live.updated', { time: new Date(live.at).toLocaleTimeString('fr-FR') })}
        </span>
      </div>
      <p className='b_book_hint'>{t('pages.b_book_pnl.live.hint')}</p>
      <BBookAlertBanners alerts={live.alerts} />

      {/* the latent tiles add up the open positions exactly like the "positions ouvertes en B" list; the engine's own house book is shown under the total and flagged when it drifts */}
      {unrealized.drift_alert && <p className='b_book_error_text' role='alert'>{t('pages.b_book_pnl.live.unrealized_drift', { symbols: (unrealized.book_mismatch || []).map(({ ticker, side, house_qty, clients_qty }) => `${ticker} ${side.toLowerCase()} ${house_qty}/${clients_qty}`).join(', ') })}</p>}
      <div className='b_book_kpis b_book_kpis_5'>
        <BBookKpi label={t('pages.b_book_pnl.live.unrealized')} value={unrealized.total} format='signed' info={t('pages.b_book_info.unrealized')}
          hint={[
            unrealized.with_spread ? t('pages.b_book_pnl.live.unrealized_client', { amount: signedMoney(unrealized.with_spread.total) }) : null,
            unrealized.engine ? t('pages.b_book_pnl.live.unrealized_engine', { amount: signedMoney(unrealized.engine.total) }) : null,
          ].filter(Boolean).join(' · ')} />
        <BBookKpi label={t('pages.b_book_pnl.live.unrealized_long')} value={unrealized.long} format='signed' info={t('pages.b_book_info.unrealized_long')} />
        <BBookKpi label={t('pages.b_book_pnl.live.unrealized_short')} value={unrealized.short} format='signed' info={t('pages.b_book_info.unrealized_short')} />
        <BBookKpi
          label={t('pages.b_book_pnl.live.realized_all_time')}
          value={realized.all_time}
          format='signed'
          info={t('pages.b_book_info.realized_all_time')}
          hint={realized.pairing_result ? t('pages.b_book_pnl.live.realized_pairing', { amount: signedMoney(realized.pairing_result) }) : t('pages.b_book_pnl.live.realized_hint')}
        />
        {funded_margin && (
          <BBookKpi label={t('pages.b_book_pnl.live.funded')} value={funded_margin.total} info={t('pages.b_book_info.funded')}
            hint={funded_margin.unpriced > 0 ? t('pages.b_book_pnl.live.funded_unpriced', { count: funded_margin.unpriced }) : fundedHint} />
        )}
      </div>

      <div className='b_book_report_grid'>
        <div className='b_book_report_card b_book_report_card_wide'>
          <BBookInfo label={t('pages.b_book_pnl.live.exposure')} text={t('pages.b_book_info.exposure')} />
          <h3>{t('pages.b_book_pnl.live.exposure')}</h3>
          <ExposureGauge exposure={live.exposure} t={t} />
        </div>
        <div className='b_book_report_card'>
          <BBookInfo label={t('pages.b_book_pnl.live.engine')} text={t('pages.b_book_info.engine')} />
          <h3>{t('pages.b_book_pnl.live.engine')}</h3>
          <div className='b_book_brakes'>
            {badges.map(({ key, text, good, hint }) => (
              <span key={key} title={hint}><Badge text={text} color={good ? 'var(--bb-ok-badge)' : 'var(--bb-alert-badge)'} /></span>
            ))}
          </div>
          {tripped.length > 0 && (
            <ul className='b_book_brake_notes'>
              {tripped.map(({ key, hint }) => <li key={key}><BBookIcon name='warning' size={14} />{hint}</li>)}
            </ul>
          )}
          <p className='b_book_pnl_engine_meta'>
            {t('pages.b_book_pnl.live.resting_orders', { count: engine.resting_orders })}
            {heartbeatAge !== null && ` · ${t('pages.b_book_pnl.live.heartbeat', { seconds: heartbeatAge })}`}
          </p>
          <Link to='/b-book' className='b_book_pnl_link'>{t('pages.b_book_pnl.live.settings_link')}</Link>
        </div>
      </div>
    </section>
  )
}

export default BBookLive
